import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useAuthContext } from "../../../../../common/AuthContext";
import { createReportAssignmentClient, reportAssignmentApi } from "./api";
import { apiErrorMessage, readyToConfirmRows } from "./logic";
import { ReportAssignmentRow, WorkspaceData } from "./types";

const keys = {
  workspace: (projectId: string) => ["report_assignment", "workspace", projectId],
  history: (projectId: string) => ["report_assignment", "history", projectId],
  readiness: (assignmentId: number | null) => ["report_assignment", "readiness", assignmentId],
  mapping: (projectId: string) => ["report_assignment", "mapping", projectId],
};

const mergeReports = (
  data: WorkspaceData | undefined,
  reports: ReportAssignmentRow[],
): WorkspaceData | undefined => {
  if (!data) return data;
  const byId = new Map(reports.map((r) => [r.id, r]));
  return { ...data, reports: data.reports.map((r) => byId.get(r.id) ?? r) };
};

/** API client bound to the signed-in user's token. */
export function useReportAssignmentApi() {
  const { state } = useAuthContext();
  return useMemo(() => reportAssignmentApi(createReportAssignmentClient(state.token)), [state.token]);
}

/**
 * Owns the Report Assignment workspace of one project: loads it from the
 * Report Assignment API and exposes the actions (create plan, choose modules,
 * confirm, confirm all, undo, finish). `initialBomId` selects the BOM tab
 * (the work order the page was opened from) when it is part of the project.
 */
export function useReportAssignmentWorkspace(projectId: string, initialBomId?: number) {
  const api = useReportAssignmentApi();
  const queryClient = useQueryClient();
  const workspaceKey = keys.workspace(projectId);

  const query = useQuery<WorkspaceData, any>(workspaceKey, () => api.workspace(projectId), {
    enabled: !!projectId,
    refetchOnWindowFocus: false,
  });
  const data = query.data;

  const [selectedBomId, setSelectedBomId] = useState<number | null>(null);
  const boms = data?.boms ?? [];
  const activeBomId =
    selectedBomId !== null && boms.some((b) => b.id === selectedBomId)
      ? selectedBomId
      : boms.find((b) => b.id === initialBomId)?.id ?? boms[0]?.id ?? 0;

  const patchReports = useCallback(
    (reports: ReportAssignmentRow[]) => {
      queryClient.setQueryData<WorkspaceData | undefined>(workspaceKey, (prev) =>
        mergeReports(prev, reports),
      );
      queryClient.invalidateQueries(keys.history(projectId));
      reports.forEach((r) => queryClient.invalidateQueries(keys.readiness(r.id)));
    },
    [queryClient, workspaceKey, projectId],
  );

  const fail = (error: any, fallback: string) => {
    toast.error(apiErrorMessage(error, fallback));
  };

  const confirmMutation = useMutation((id: number) => api.confirm(id), {
    onSuccess: (res) => patchReports([res.report]),
    onError: (e) => fail(e, "Could not confirm the report"),
  });

  const confirmAllMutation = useMutation((bomId: number) => api.confirmAll(projectId, bomId), {
    onSuccess: (res) => patchReports(res.reports),
    onError: (e) => fail(e, "Could not confirm the reports"),
  });

  const modulesMutation = useMutation(
    (v: { id: number; unitIds: number[]; reason?: string }) =>
      api.setModules(v.id, v.unitIds, v.reason),
    { onSuccess: (res) => patchReports([res.report]) },
  );

  const undoMutation = useMutation((historyId: number) => api.undo(historyId), {
    onSuccess: (res) => patchReports([res.report]),
    onError: (e) => fail(e, "Could not undo the change"),
  });

  const finishMutation = useMutation(() => api.finish(projectId), {
    onSuccess: () => queryClient.invalidateQueries(workspaceKey),
    onError: (e) => fail(e, "Could not finish report assignment"),
  });

  /** Each action resolves with the ids of the history entries it logged (for the undo toast). */
  const confirmReport = useCallback(
    async (reportId: number): Promise<number[]> => {
      try {
        const res = await confirmMutation.mutateAsync(reportId);
        return res.historyEntry ? [res.historyEntry.id] : [];
      } catch {
        return []; // reported through onError
      }
    },
    [confirmMutation],
  );

  const confirmAllReadyInBom = useCallback(
    async (bomId: number): Promise<number[]> => {
      try {
        const res = await confirmAllMutation.mutateAsync(bomId);
        return res.historyEntries.map((e) => e.id);
      } catch {
        return []; // reported through onError
      }
    },
    [confirmAllMutation],
  );

  /** Resolves with the history entry ids; rejects with the API error so the picker can show it. */
  const saveModuleSelection = useCallback(
    async (reportId: number, unitIds: number[], reason?: string): Promise<number[]> => {
      const res = await modulesMutation.mutateAsync({ id: reportId, unitIds, reason });
      return res.historyEntry ? [res.historyEntry.id] : [];
    },
    [modulesMutation],
  );

  const undoChanges = useCallback(
    async (historyIds: number[]) => {
      for (const id of historyIds) {
        try {
          await undoMutation.mutateAsync(id);
        } catch {
          return; // reported through onError
        }
      }
    },
    [undoMutation],
  );

  const undoChange = useCallback((historyId: number) => undoChanges([historyId]), [undoChanges]);

  const finish = useCallback(async () => {
    try {
      await finishMutation.mutateAsync();
    } catch {
      // reported through onError
    }
  }, [finishMutation]);

  const reports = data?.reports ?? [];
  const rowsForBom = useCallback(
    (bomId: number) => reports.filter((r) => r.bomId === bomId),
    [reports],
  );
  const readyRowsForBom = useCallback(
    (bomId: number) => readyToConfirmRows(rowsForBom(bomId)),
    [rowsForBom],
  );

  return {
    project: data?.project,
    needsPlan: data?.needsPlan ?? false,
    finished: data?.finished ?? null,
    reportTypes: data?.reportTypes ?? [],
    boms,
    modules: data?.modules ?? [],
    rows: reports,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    activeBomId,
    setActiveBomId: setSelectedBomId,
    rowsForBom,
    readyRowsForBom,
    confirmReport,
    confirmAllReadyInBom,
    saveModuleSelection,
    undoChange,
    undoChanges,
    finish,
    isMutating:
      confirmMutation.isLoading ||
      confirmAllMutation.isLoading ||
      undoMutation.isLoading ||
      finishMutation.isLoading,
    isFinishing: finishMutation.isLoading,
  };
}

export type ReportAssignmentWorkspace = ReturnType<typeof useReportAssignmentWorkspace>;

/** Project-wise report mapping: every available report and the ones that apply to each BOM. */
export function useProjectReports(projectId: string, enabled = true) {
  const api = useReportAssignmentApi();
  const state = useQuery(keys.mapping(projectId), () => api.projectReports(projectId), {
    enabled: enabled && !!projectId,
    refetchOnWindowFocus: false,
  });
  return state;
}

/** Change history of the project's reports (only loaded while the dialog is open). */
export function useReportAssignmentHistory(projectId: string, enabled: boolean) {
  const api = useReportAssignmentApi();
  return useQuery(keys.history(projectId), () => api.history(projectId), {
    enabled: enabled && !!projectId,
    refetchOnWindowFocus: false,
  });
}

/** Per-module data-ready detail of one report. */
export function useReportReadiness(assignmentId: number | null) {
  const api = useReportAssignmentApi();
  return useQuery(keys.readiness(assignmentId), () => api.readiness(assignmentId as number), {
    enabled: assignmentId !== null,
    refetchOnWindowFocus: false,
  });
}
